import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, Heart, Menu, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { Button } from './ui/button';

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const { getCartCount } = useCart();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="bg-white border-b border-stone-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <div className="bg-purple-100 px-6 py-3 rounded-full">
              <h1 className="text-xl font-heading font-light text-purple-800" data-testid="logo">
                WWW.HealerMarah.com
              </h1>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6">
            <Link to="/" className="text-sm uppercase tracking-widest hover:text-primary transition-colors" data-testid="nav-home">
              Home
            </Link>
            
            {/* Products Dropdown */}
            <div className="relative group">
              <button className="text-sm uppercase tracking-widest hover:text-primary transition-colors flex items-center" data-testid="nav-products">
                Products
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className="absolute left-0 mt-2 w-56 bg-white border border-stone-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-2">
                  <Link to="/product/sac-sg" className="block px-4 py-2 text-sm hover:bg-stone-50 transition-colors" data-testid="nav-sac-sg">
                    SAC-SG
                  </Link>
                  <Link to="/product/marah-cel" className="block px-4 py-2 text-sm hover:bg-stone-50 transition-colors" data-testid="nav-marah-cel">
                    MARAH-CEL
                  </Link>
                  <Link to="/product/rectrail" className="block px-4 py-2 text-sm hover:bg-stone-50 transition-colors" data-testid="nav-rectrail">
                    RECTRAIL
                  </Link>
                  <Link to="/product/dentosso" className="block px-4 py-2 text-sm hover:bg-stone-50 transition-colors" data-testid="nav-dentosso">
                    DENTOSSO
                  </Link>
                  <Link to="/product/alzi-cel" className="block px-4 py-2 text-sm hover:bg-stone-50 transition-colors" data-testid="nav-alzi-cel">
                    ALZI-CEL
                  </Link>
                  <Link to="/product/teen-cel" className="block px-4 py-2 text-sm hover:bg-stone-50 transition-colors" data-testid="nav-teen-cel">
                    TEEN-CEL
                  </Link>
                  <Link to="/product/kids-cel" className="block px-4 py-2 text-sm hover:bg-stone-50 transition-colors" data-testid="nav-kids-cel">
                    KIDS-CEL
                  </Link>
                  <Link to="/product/neo-cel" className="block px-4 py-2 text-sm hover:bg-stone-50 transition-colors" data-testid="nav-neo-cel">
                    NEO-CEL
                  </Link>
                </div>
              </div>
            </div>
            
            <Link to="/shop" className="text-sm uppercase tracking-widest hover:text-primary transition-colors" data-testid="nav-shop">
              Shop All
            </Link>
            <Link to="/about" className="text-sm uppercase tracking-widest hover:text-primary transition-colors" data-testid="nav-about">
              About our Products
            </Link>
            <Link to="/science" className="text-sm uppercase tracking-widest hover:text-primary transition-colors" data-testid="nav-science">
              The Science
            </Link>
          </div>

          {/* Icons */}
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/search')}
              data-testid="search-button"
            >
              <Search className="h-5 w-5" />
            </Button>
            
            {user && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/wishlist')}
                data-testid="wishlist-button"
              >
                <Heart className="h-5 w-5" />
              </Button>
            )}

            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/cart')}
              className="relative"
              data-testid="cart-button"
            >
              <ShoppingCart className="h-5 w-5" />
              {getCartCount() > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-white text-xs w-5 h-5 rounded-full flex items-center justify-center" data-testid="cart-count">
                  {getCartCount()}
                </span>
              )}
            </Button>

            {user ? (
              <div className="hidden md:flex items-center space-x-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate('/dashboard')}
                  data-testid="dashboard-button"
                >
                  <User className="h-5 w-5" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  className="rounded-full"
                  data-testid="logout-button"
                >
                  Logout
                </Button>
              </div>
            ) : (
              <Button
                variant="default"
                size="sm"
                onClick={() => navigate('/login')}
                className="hidden md:flex rounded-full"
                data-testid="login-button"
              >
                Login
              </Button>
            )}

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X /> : <Menu />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-stone-200" data-testid="mobile-menu">
          <div className="px-6 py-4 space-y-3">
            <Link
              to="/"
              className="block py-2 text-sm uppercase tracking-widest"
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </Link>
            
            {/* Products Section */}
            <div className="py-2">
              <p className="text-xs uppercase tracking-widest text-stone-500 mb-2">Products</p>
              <div className="pl-4 space-y-2">
                <Link
                  to="/product/sac-sg"
                  className="block py-1 text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  SAC-SG
                </Link>
                <Link
                  to="/product/marah-cel"
                  className="block py-1 text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  MARAH-CEL
                </Link>
                <Link
                  to="/product/rectrail"
                  className="block py-1 text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  RECTRAIL
                </Link>
                <Link
                  to="/product/dentosso"
                  className="block py-1 text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  DENTOSSO
                </Link>
                <Link
                  to="/product/alzi-cel"
                  className="block py-1 text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  ALZI-CEL
                </Link>
                <Link
                  to="/product/teen-cel"
                  className="block py-1 text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  TEEN-CEL
                </Link>
                <Link
                  to="/product/kids-cel"
                  className="block py-1 text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  KIDS-CEL
                </Link>
                <Link
                  to="/product/neo-cel"
                  className="block py-1 text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  NEO-CEL
                </Link>
              </div>
            </div>
            
            <Link
              to="/shop"
              className="block py-2 text-sm uppercase tracking-widest"
              onClick={() => setMobileMenuOpen(false)}
            >
              Shop All
            </Link>
            <Link
              to="/about"
              className="block py-2 text-sm uppercase tracking-widest"
              onClick={() => setMobileMenuOpen(false)}
            >
              About our Products
            </Link>
            <Link
              to="/science"
              className="block py-2 text-sm uppercase tracking-widest"
              onClick={() => setMobileMenuOpen(false)}
            >
              The Science
            </Link>
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  className="block py-2 text-sm uppercase tracking-widest"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
                <button
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left py-2 text-sm uppercase tracking-widest"
                >
                  Logout
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="block py-2 text-sm uppercase tracking-widest"
                onClick={() => setMobileMenuOpen(false)}
              >
                Login
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};